import { 
  Vector3, 
  BufferGeometry, 
  BufferAttribute, 
  ShaderMaterial, 
  Points, 
  PlaneGeometry,
  Mesh,
  LinearFilter,
  Vector2,
  DoubleSide,
  MathUtils,
  AdditiveBlending,
  BoxGeometry,
  BackSide,
  Quaternion
} from 'three';

import galaxyTextureImg from '../../../assets/textures/galaxy/galaxy.png';
import vertexNebula from '../../../shaders/nebula/vertex.glsl';
import fragmentNebula from '../../../shaders/nebula/fragment.glsl';
import {smoothstep } from '../helpers.js';

import { animatePartsGalaxy } from '../utils/many-bodies.js';
import vertexGalaxy from '../../../shaders/galaxy/vertex.glsl';
import fragmentGalaxy from '../../../shaders/galaxy/fragment.glsl';

import vertexBgLocal from '../../../shaders/background/vertex.glsl';
import fragmentBgLocal from '../../../shaders/background/fragment.glsl';

import { loading } from '../loading/loading.js';
import {sceneManager } from '../script.js';


import { OpaqueNebula } from './nebulaOpaque.js';

import {
  ANGLE_ROT_WARP,
  INCLIN_GALAXY,
  MAX_WARP_ANGLE,
  NEB_MAX_SPEED,
  NEB_MIN_SPEED,
  NEB_MAX_OFFSET,
  NEB_MIN_OFFSET,
  NEB_MAX_SIZE,
  NEB_MIN_SIZE,
  RADIUS_GALAXY,
  WAVE_SPEED,
  WAVE_START,
  NEB_MAX_SIZE
} from '../configEntities/galaxyConfig.js';

import nx_l_bg from '../../../assets/scenario/stars_milkway_bg/nx.png';
import ny_l_bg from '../../../assets/scenario/stars_milkway_bg/ny.png';
import nz_l_bg from '../../../assets/scenario/stars_milkway_bg/nz.png';
import px_l_bg from '../../../assets/scenario/stars_milkway_bg/px.png';
import py_l_bg from '../../../assets/scenario/stars_milkway_bg/py.png';
import pz_l_bg from '../../../assets/scenario/stars_milkway_bg/pz.png';


export class Nebula {
  constructor(centerElement) {
    this.numPoints = 2400;
    this.warpPhase = 0
    this.time = 0
    this.blending = AdditiveBlending;
    this.centerElement = centerElement;
    this.galaxyNormal = new Vector3(0, Math.cos(-1.0507), Math.sin(-1.0507)).normalize();
    this.opaqueNeb = new OpaqueNebula(2000, centerElement)
    this.positions = new Float32Array(this.numPoints * 3);
    this.sizes = new Float32Array(this.numPoints);
    this.randValue = new Float32Array(this.numPoints);
    this.angles = new Float32Array(this.numPoints);
    this.speeds = [];

    this.geometry = new BufferGeometry();
    this._generateAttr();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
   
    this.geometry.setAttribute('randomValue', new BufferAttribute(this.randValue, 1));
    this.geometry.setAttribute('angle', new BufferAttribute(this.angles, 1));
    this.originalPositions = this.geometry.attributes.position.array.slice();
    this._loadTexture();

    this.uniforms = {
      opacity: { value: 0.0 },
      u_tex: { value: this.textureMap },
      u_r: { value: RADIUS_GALAXY * 2 },
      u_time: { value: 0.0 },
      cameraPos:   { value: sceneManager.camera.position },
      diskCenter:  { value: this.centerElement.mesh.position },
      diskNormal:  { value: this.galaxyNormal },

      minRadius:   { value: RADIUS_GALAXY * 0.1 },
      maxRadius:   { value: RADIUS_GALAXY * 2 },
      maxCamDist:  { value: RADIUS_GALAXY * 3 },

      minThick:    { value: NEB_MAX_SIZE*0.05 },
      maxThick:    { value: NEB_MAX_SIZE},
      falloffExp:  { value: 1.0 },
      falloffExpMajor:  { value: 3 }, 
      fadePlane: {value:RADIUS_GALAXY * 0.05},
      fadeThick: {value:NEB_MAX_SIZE * 0.5},
      u_probability:{value:0.8}
      };

    this.pointMaterial = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vertexNebula,
      fragmentShader: fragmentNebula,
      blending: this.blending,
      transparent: false,
      depthWrite: false,
      depthTest: true,
    });

    this.points = new Points(this.geometry, this.pointMaterial);
     //this.points.renderOrder = 2
    this.points.rotation.x = INCLIN_GALAXY;
    sceneManager.scene.add(this.points);

    this._setupPlane();
    this._createBoxBG()
  }
  _generateAttr() {
    for (let i = 0; i < this.numPoints; i++) {
      const r = Math.pow(Math.random(), 0.6) * RADIUS_GALAXY;
      const theta = Math.random() * Math.PI * 2;
      const x = r * Math.cos(theta);
      const z = r * Math.sin(theta);
      const dUV = r / RADIUS_GALAXY;
      const f1 = 1 - smoothstep(0, 0.3, dUV, 1.5);
      const f2 = 1 - smoothstep(0, 1, dUV, 5);
      const yBase = NEB_MAX_OFFSET * f1 + NEB_MIN_OFFSET * f2;
      const y = (Math.random() * 2 - 1) * yBase;
      this.positions[3 * i] = x;
      this.positions[3 * i + 1] = y;
      this.positions[3 * i + 2] = z;
      this.randValue[i] = Math.random();
      this.angles[i] = Math.PI - theta;
      this.speeds.push(
        Math.random() * (NEB_MAX_SPEED - NEB_MIN_SPEED) + NEB_MIN_SPEED
      );
    }
  }

_createBoxBG() {
  this.speedBg = -9.016846e-11;
  const envMap = sceneManager.cubeTextureLoader.load([
    px_l_bg, nx_l_bg,
    py_l_bg, ny_l_bg,
    pz_l_bg, nz_l_bg
  ]);
  const skyMat = new ShaderMaterial({
    uniforms: {
      envMap:   { value: envMap },
      opacity:  { value: 1 }
    },
    vertexShader: vertexBgLocal,
    fragmentShader:fragmentBgLocal,
    side:           BackSide,
    depthWrite:     false,
    blending:       AdditiveBlending
  });
  this.skybox = new Mesh(new BoxGeometry(1e10, 1e10, 1e10), skyMat);
  this.skybox.rotation.set(1.48, 18.7, 10.3);
  sceneManager.scene.add(this.skybox);
}
update_bg(speed) {
  const animSpeed = speed * this.speedBg;
  this.skybox.quaternion.premultiply(
    new Quaternion().setFromAxisAngle(this.galaxyNormal, animSpeed)
  );
  const d = sceneManager.camera.position.length();
  const x = (1e7 - d) / 1e7;
  const t = x < 0 ? 0 : x > 1 ? 1 : x;
  const s = t * t * (3 - 2 * t);
  this.skybox.material.uniforms.opacity.value = s;
}

_loadTexture() {
  this.textureMap = loading.textureLoader
    .load(
      galaxyTextureImg,
      () => this._filterAndUpdate()
    );
}

  _filterAndUpdate() {
    const img = this.textureMap.image;
    const [w, h] = [img.width, img.height];
    const cvs = document.createElement('canvas');
    cvs.width = w;
    cvs.height = h;
    const ctx = cvs.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const imgData = ctx.getImageData(0, 0, w, h).data;
    const u_r = RADIUS_GALAXY * 2;
    for (let i = 0; i < this.numPoints; i++) {
      const ix = i * 3;
      const x = this.positions[ix],
      z = this.positions[ix + 2];
      const uvx = x / u_r + 0.5;
      const uvz = z / u_r + 0.5;
      const cx = Math.min(Math.max(Math.floor(uvx * w), 0), w - 1);
      const cz = Math.min(Math.max(Math.floor(uvz * h), 0), h - 1);
      const bi = (cz * w + cx) * 4;
      const bright =
        (0.299 * imgData[bi] + 0.587 * imgData[bi + 1] +
          0.114 * imgData[bi + 2]) /
        255;
        const size = NEB_MIN_SIZE + bright * (NEB_MAX_SIZE - NEB_MIN_SIZE);
        this.sizes[i] = size;
    }
     this.geometry.setAttribute('sizes', new BufferAttribute(this.sizes, 1));
  }
  _setupPlane() {
    loading.textureLoader.load(galaxyTextureImg, tex => {
      tex.minFilter = LinearFilter; tex.magFilter = LinearFilter; tex.generateMipmaps = true;
      const seg = 128;
      const geom = new PlaneGeometry(RADIUS_GALAXY*2, RADIUS_GALAXY*2, seg, seg);
      geom.rotateX(-Math.PI/2);
      this.planeOriginalPositions = geom.attributes.position.array.slice();
      const mat = new ShaderMaterial({
        uniforms: {
          u_tex: { value: tex },
          noiseScale: { value: 10 },
          noiseStrength: { value: 0.05 },
          blurStrength: { value: 0.5 },
          u_texelSize: { value: new Vector2(1/tex.image.width,1/tex.image.height) },
          opacity: { value: 0 },
          u_radius:     { value: 1000 } 
        },
        vertexShader: vertexGalaxy,
        fragmentShader: fragmentGalaxy,
        blending: this.blending,

        transparent: false,
        depthWrite: false,
        depthTest: true,
        side: DoubleSide,
      });
      this.mesh = new Mesh(geom, mat);
      this.mesh.rotation.x = INCLIN_GALAXY;
      sceneManager.scene.add(this.mesh);
    });
  }
  centerOrbit() {
    this.points.position.copy(this.centerElement.mesh.position);

    if (this.mesh) {
      this.mesh.position.copy(this.centerElement.mesh.position);
    }
  }

  animate(speed) {
    this.warpPhase = (this.warpPhase + speed * WAVE_SPEED) % (2 * Math.PI);

    const camPos = sceneManager.camera.position;
    const distance = camPos.length()/RADIUS_GALAXY;
    this.centerOrbit()
    this.opaqueNeb.centerOrbit()
    this.opaqueNeb.animate(speed)
    if (this.points) {
      animatePartsGalaxy(
        this.points,
        speed,
        this.warpPhase,
        this.speeds,
        this.originalPositions
      )
   
  const renderPart= this.calculateOpacityGalaxy(sceneManager.camera, this.centerElement.mesh.position, this.galaxyNormal, 0.15, 0.1, 0.1)
  this.points.material.uniforms.u_time.value += speed*2e-9
  this.pointMaterial.uniforms.cameraPos.value.copy(sceneManager.camera.position);
  this.pointMaterial.uniforms.diskCenter.value.copy(this.centerElement.mesh.position);

  this.points.material.uniforms.opacity.value = renderPart

    }
    if (this.mesh) {
      const pos = this.mesh.geometry.attributes.position;
      const arr = pos.array;
        for (let i=0; i<arr.length; i+=3) {
        const x0 = this.planeOriginalPositions[i], y0 = this.planeOriginalPositions[i+1],z0 = this.planeOriginalPositions[i+2];
        const r = Math.hypot(x0,z0);
        const orbitalAngle0 = Math.atan2(z0, x0);
        const theta = orbitalAngle0 + NEB_MIN_SPEED * speed;
        arr[i]   =  r * Math.cos(theta);
        arr[i+2] =  r * Math.sin(theta);

        let y = y0;
        if (r > WAVE_START) {
          const t = (r - WAVE_START)/(RADIUS_GALAXY - WAVE_START);
          const bend = t * MAX_WARP_ANGLE;
          const off = Math.sin(bend)*(RADIUS_GALAXY*t);
          const phase = (orbitalAngle0 + this.warpPhase + ANGLE_ROT_WARP)%(Math.PI*2);
          y += off * Math.cos(phase);
        }
        arr[i+1] = y;
        this.planeOriginalPositions[i] = arr[i]
        this.planeOriginalPositions[i+2] = arr[i+2]
      }
      pos.needsUpdate = true;


      if(this.skybox)
      this.update_bg(speed)
    
      this.mesh.material.uniforms.opacity.value = this.calculateOpacityGalaxy(sceneManager.camera, this.centerElement.mesh.position, this.galaxyNormal, 0.7, 0, 0)*distance;
    }
     
  }
  calculateOpacityGalaxy(camera, galaxyCenter, galaxyNormal, a, b, liMin) {
  const camDir = new Vector3().subVectors(camera.position, galaxyCenter).normalize();
  const dot = Math.abs(camDir.dot(galaxyNormal));
  const dist = camera.position.distanceTo(galaxyCenter);
  const dNorm = MathUtils.clamp(dist / RADIUS_GALAXY, 0, 1);
  const angleOpacity = a * dot + b;
  const proximityOpacity = Math.max(liMin, 1 - (1 - dNorm)) ;
  const opacity = angleOpacity * proximityOpacity;
  return opacity;
}
}

